/**
 * Application configuration
 * Centralizes environment variables and provides typed access
 */

// =============================================================================
// Network Configuration
// =============================================================================

export type Network = 'sandbox' | 'ghostnet' | 'mainnet';

export const NETWORK: Network = (import.meta.env.VITE_NETWORK || 'ghostnet') as Network;

export const RPC_URL = import.meta.env.VITE_RPC_URL || getRpcUrlForNetwork(NETWORK);

// Sandbox RPC only available in development - use PROD check for proper dead code elimination
const SANDBOX_RPC_URL = import.meta.env.PROD ? 'https://mainnet.api.tez.ie' : 'http://localhost:20000';

function getRpcUrlForNetwork(network: Network): string {
  switch (network) {
    case 'sandbox':
      return SANDBOX_RPC_URL;
    case 'ghostnet':
      // Use teztnets RPC - has CORS enabled (marigold.dev does not)
      return 'https://rpc.ghostnet.teztnets.com';
    case 'mainnet':
      return 'https://mainnet.api.tez.ie';
    default:
      return 'https://rpc.ghostnet.teztnets.com';
  }
}

// =============================================================================
// Contract Addresses
// =============================================================================

export const MARKETPLACE_CONTRACT = import.meta.env.VITE_MARKETPLACE_CONTRACT || '';

// =============================================================================
// API Configuration
// =============================================================================

/**
 * Base URL for the API server.
 * - Empty string means same-origin (production behind same domain/Nginx)
 * - Full URL for cross-origin deployments (e.g., https://api.example.com)
 */
export const API_BASE_URL = import.meta.env.VITE_API_URL || '';

/**
 * Get the full API URL for a given path
 * Handles both same-origin and cross-origin deployments
 */
export function getApiUrl(path: string): string {
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  if (API_BASE_URL) {
    // Cross-origin: prepend the base URL
    return `${API_BASE_URL}${normalizedPath}`;
  }

  // Same-origin: use relative path
  return normalizedPath;
}

// =============================================================================
// IPFS Configuration
// =============================================================================

export const IPFS_GATEWAY = import.meta.env.VITE_IPFS_GATEWAY || 'https://ipfs.io/ipfs/';

// NFT.storage for pinning
export const NFT_STORAGE_KEY = import.meta.env.VITE_NFT_STORAGE_KEY || '';

// Pinata for pinning (alternative)
export const PINATA_API_KEY = import.meta.env.VITE_PINATA_API_KEY || '';
export const PINATA_SECRET_KEY = import.meta.env.VITE_PINATA_SECRET_KEY || '';

/**
 * Convert an IPFS URI to a gateway URL
 */
export function ipfsToHttp(uri: string): string {
  if (!uri) return '';

  // Already HTTP(S)
  if (uri.startsWith('http://') || uri.startsWith('https://')) {
    return uri;
  }

  // ipfs:// protocol
  if (uri.startsWith('ipfs://')) {
    const cid = uri.slice(7);
    return `${IPFS_GATEWAY}${cid}`;
  }

  // Raw CID
  if (uri.startsWith('Qm') || uri.startsWith('bafy')) {
    return `${IPFS_GATEWAY}${uri}`;
  }

  return uri;
}

/**
 * Get thumbnail URL for an IPFS CID via our API
 */
export function getThumbnailUrl(cid: string, size: number = 256): string {
  if (!cid) return '';

  // If it's already an HTTP URL, return as-is (can't thumbnail external URLs)
  if (cid.startsWith('http://') || cid.startsWith('https://')) {
    return cid;
  }

  // Extract CID from URI if needed
  let cleanCid = cid;
  if (cid.startsWith('ipfs://')) {
    cleanCid = cid.slice(7);
  } else if (cid.includes('/ipfs/')) {
    cleanCid = cid.split('/ipfs/')[1];
  }

  // Use our thumbnail API
  return getApiUrl(`/api/thumb/${cleanCid}?w=${size}`);
}

// =============================================================================
// TzKT Configuration
// =============================================================================

export const TZKT_API = import.meta.env.VITE_TZKT_API || getTzktUrlForNetwork(NETWORK);

function getTzktUrlForNetwork(network: Network): string {
  switch (network) {
    case 'ghostnet':
      return 'https://api.ghostnet.tzkt.io';
    case 'mainnet':
      return 'https://api.tzkt.io';
    default:
      return 'https://api.tzkt.io';
  }
}

// =============================================================================
// Marketplace Fee Configuration
// =============================================================================

/**
 * Marketplace fee percentage (1% = 100 basis points)
 * This fee is added to all purchases made through the marketplace
 */
export const MARKETPLACE_FEE_PERCENT = 1;

/**
 * Wallet address to receive marketplace fees
 */
export const MARKETPLACE_FEE_WALLET = 'tz1aLvmWNq1SbA8mTktMs9NwogU7rf8FiZHf';

/**
 * Calculate marketplace fee in mutez
 * @param priceMutez - Total price in mutez
 * @returns Fee amount in mutez
 */
export function calculateMarketplaceFee(priceMutez: string | bigint): bigint {
  const price = typeof priceMutez === 'string' ? BigInt(priceMutez) : priceMutez;
  // fee = price * MARKETPLACE_FEE_PERCENT / 100
  return (price * BigInt(MARKETPLACE_FEE_PERCENT)) / BigInt(100);
}

// =============================================================================
// Feature Flags
// =============================================================================

export const IS_PRODUCTION = import.meta.env.PROD;
export const IS_DEVELOPMENT = import.meta.env.DEV;

/**
 * Check if IPFS pinning is configured
 */
export function hasIpfsPinning(): boolean {
  return !!(NFT_STORAGE_KEY || (PINATA_API_KEY && PINATA_SECRET_KEY));
}

// =============================================================================
// Debug Info
// =============================================================================

if (IS_DEVELOPMENT) {
  console.log('[Config] Network:', NETWORK);
  console.log('[Config] RPC URL:', RPC_URL);
  console.log('[Config] API Base:', API_BASE_URL || '(same-origin)');
  console.log('[Config] TzKT:', TZKT_API);
  console.log('[Config] IPFS Pinning:', hasIpfsPinning() ? 'enabled' : 'disabled');
}
